import React, { useContext, useEffect, useState } from 'react';
import { SocialProfileSelector } from '../../../components/socialProfiles/SocialProfileSelector';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Select } from '../../../components/forms/Select';
import { InputErrorMessageSeverityType, SelectItem } from '../../../components/forms/Select/Select.view';
import { getCategorySelectTemplates, MappedCategoriesType } from '../../Publish/Categories/Categories.helpers';
import classNames from 'classnames';
import classes from './CategoryProfilesSelector.module.scss';
import { CategoryFieldsFragment } from '../../../models/graphql';
import { PostEditorContext } from '../states/PostEditorProvider';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { mapProfileFragmentToSelectedSocialProfileValue } from '../helpers/mapProfileFragmentToSelectedSocialProfileValue';
import { hasSeverity } from '../validators/helpers/severity-checker';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { ErrorMessageSeverity } from '../../../constants/form';
import { PostEditorValidationMessages } from '../constants/validation-messages';
import { getErrorMessages } from '../validators/helpers/getErrorMessages';
import { setProfileSelectorFormValues } from '../../../components/socialProfiles/SocialProfileSelector/SocialProfileSelector.view';
import pluralize from 'pluralize';
import { POST_EDITOR_ERROR_CLASS } from '../../../constants/styleConstants';
import { isPostEditorActive } from '../../Onboarding/helpers/isPostEditorActive';
import { OnboardingContext } from '../../Onboarding/states/OnboardingProvider';
import { useUserData } from '../../../hooks/useUserData';
import { AppContext } from '../../../providers/AppProvider';
// import { TourTip } from '../../Onboarding/TourTip';
import { useBreakpoints } from '../../../hooks/useBreakpoints';

export type CategoryProfilesSelectorViewProps = {
  //
  categoriesFieldName: string;
  dontSetDefaultProfiles?: boolean;
  formInstance: UseFormReturn;
  profilesFieldName: string;
  selectedCategoryId?: string;
  selectedProfiles?: SelectedSocialProfileValue[];
  selectAllFormName: string;
  disabledProfileIds?: string[];
  disableCategorySelector?: boolean;
  disableProfilesSelector?: boolean;
};

export const CategoryProfilesSelectorView = (props: CategoryProfilesSelectorViewProps): JSX.Element => {
  const {
    formInstance,
    profilesFieldName,
    categoriesFieldName,
    selectedCategoryId,
    selectAllFormName,
    dontSetDefaultProfiles,
    disabledProfileIds,
    disableCategorySelector,
    disableProfilesSelector
  } = props;
  const { postEditorContext } = useContext(AppContext);
  const { getValues } = formInstance;
  const { state: onboardingState, showTourTips } = useContext(OnboardingContext);
  const { profiles, categories, state } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const breakpoints = useBreakpoints();
  const isMobile = breakpoints === 'small';
  const [tourtipCategoryProfileDesktopXY, setTourtipCategoryProfileDesktopXY] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const { userData } = useUserData();
  const { onboarding } = userData || {};
  const { stepsSeen } = onboarding || {};
  const { version } = onboardingState;
  const { postGroupIndex } = tabsState;
  const postGroup = state.postGroups[postGroupIndex];
  const [selectedProfiles, setSelectedProfiles] = useState<SelectedSocialProfileValue[] | undefined>(
    props.selectedProfiles
  );
  const [valueTemplate, itemTemplate] = getCategorySelectTemplates(categories);
  const mappedCategories: SelectItem[] = categories.map((category: MappedCategoriesType) => ({
    label: category.name,
    value: category.id
  }));

  const selectedCategory: string = useWatch({
    control: formInstance.control,
    name: categoriesFieldName,
    defaultValue: selectedCategoryId || ''
  });

  //watch for category change and update selected profiles.
  // TODO move this to post editor update effect?
  useEffect(() => {
    const profilesFormValue: (string | boolean)[] = getValues(profilesFieldName) || [];
    const hasSelectedProfiles = profilesFormValue.filter(p => !!p).length > 0;
    const defaultCategoryProfiles: CategoryFieldsFragment['defaultSocialProfiles'] =
      categories.find(c => c.id === selectedCategory)?.defaultSocialProfiles ?? [];

    if (hasSelectedProfiles || !selectedCategory || dontSetDefaultProfiles || defaultCategoryProfiles.length === 0) {
      return;
    }

    const mappedDefaultProfiles = mapProfileFragmentToSelectedSocialProfileValue(defaultCategoryProfiles);
    setSelectedProfiles(mappedDefaultProfiles);
    setProfileSelectorFormValues(profiles, mappedDefaultProfiles, formInstance, profilesFieldName);
  }, [selectedCategory, getValues, profilesFieldName, dontSetDefaultProfiles, categories, formInstance, profiles]);

  //in case we externally change selectedProfiles, make sure we update state
  useEffect(() => {
    setSelectedProfiles(props.selectedProfiles);
  }, [props.selectedProfiles]);

  const { errors } = postGroup;
  const hasProfileError = hasSeverity(errors, ErrorMessageSeverity.error, null, null, [
    'pinterestBoardRequired',
    'profileRequired',
    'tikTokAudienceRequired'
  ]);
  const hasProfileWarning = hasSeverity(errors, ErrorMessageSeverity.warning, null, null, [
    'pinterestBoardRequired',
    'profileRequired',
    'tikTokAudienceRequired'
  ]);

  const hasPinterestTwitterProfileError = hasSeverity(errors, ErrorMessageSeverity.error, undefined, undefined, [
    'pinterestDuplicatesCheck',
    'preventTwDuplicate'
  ]);

  const errorsProfile = getErrorMessages(errors, null, null, [
    'pinterestBoardRequired',
    'profileRequired',
    'tikTokAudienceRequired'
  ]);

  const errorsPinterestTwitter = hasPinterestTwitterProfileError
    ? getErrorMessages(errors, undefined, undefined, ['pinterestDuplicatesCheck', 'preventTwDuplicate']).join('\n')
    : '';

  const hasCategoryError = hasSeverity(errors, ErrorMessageSeverity.error, null, null, ['categoryRequired']);

  const columnClasses = classNames(
    'cell',
    'small-12',
    'medium-6',
    'margin-bottom-small-8',
    'margin-bottom-medium-0',
    classes.CategoryProfilesSelector
  );

  let keyCategoryProfiles = '';
  if (isPostEditorActive(postEditorContext) && showTourTips && stepsSeen) {
    switch (version) {
      case 1:
      default: {
        keyCategoryProfiles = 'v1_tourtip_post_editor_select_category';
        break;
      }
    }
  }

  const getQtyProfilesInvalid = () => {
    if (!selectedProfiles || selectedProfiles.length === 0) {
      return 0;
    }

    return selectedProfiles.reduce<number>((currentSum, currentProfile) => {
      if (currentProfile.__typename === 'TikTokSocialProfile' && !currentProfile.tikTok?.privacyTarget) {
        return currentSum + 1;
      }
      if (currentProfile.__typename === 'PinterestSocialProfile' && !currentProfile.pnBoardId) {
        return currentSum + 1;
      }
      return currentSum;
    }, 0);
  };

  const getInputErrorMessageSeverityType = (
    errorMessageArr: string[],
    errorSeverity: ErrorMessageSeverity
  ): InputErrorMessageSeverityType => {
    let errorMessage: JSX.Element | string;
    const qtyProfile = getQtyProfilesInvalid();
    if (errorMessageArr.length < 2) {
      errorMessage = errorMessageArr[0] || '';
      if (errorMessage === PostEditorValidationMessages.tikTokAudienceRequired) {
        errorMessage =
          qtyProfile === 1
            ? 'Select an Audience for TikTok profile'
            : `Select an Audience for ${qtyProfile} TikTok profiles`;
      }
      if (errorMessage === PostEditorValidationMessages.pinterestBoardRequired) {
        errorMessage =
          qtyProfile === 1
            ? 'Select a Board for Pinterest profile'
            : `Select a Board for ${qtyProfile} Pinterest profiles`;
      }
    } else {
      errorMessage = `${pluralize('profile', getQtyProfilesInvalid(), true)} require attention.`;
    }
    return {
      errorMessage,
      errorSeverity
    };
  };

  return (
    <div
      className={'grid-x grid-padding-x relative'}
      ref={ref => {
        if (ref && !tourtipCategoryProfileDesktopXY) {
          const width = isMobile
            ? ref.getBoundingClientRect().width
            : Array.from(ref.children).reduce((prev, current) => prev + current.getBoundingClientRect().width, 0);
          const top = isMobile ? 18 : 52;
          const left = isMobile ? width - 85 : width / 2 - 8;
          setTourtipCategoryProfileDesktopXY({ top, left });
        }
      }}>
      <div className={classNames(columnClasses, hasCategoryError ? POST_EDITOR_ERROR_CLASS : null)}>
        <Select
          chooseLabel={'Select a Category'}
          hideDefaultSelectOption={true}
          items={mappedCategories}
          name={categoriesFieldName}
          size={'large'}
          rules={{}}
          valueTemplate={valueTemplate}
          itemTemplate={itemTemplate}
          disabled={disableCategorySelector}
          useFormInstance={formInstance}
          {...(selectedCategoryId ? { selectedValue: selectedCategoryId } : {})}
          {...(hasCategoryError
            ? getInputErrorMessageSeverityType(
                [PostEditorValidationMessages.categoryRequired],
                ErrorMessageSeverity.error
              )
            : {})}
        />
      </div>
      <div className={classNames(columnClasses, hasProfileError ? POST_EDITOR_ERROR_CLASS : null)}>
        <SocialProfileSelector
          usePinterestBoard={true}
          groupByProfile={false}
          useTikTokSettings={true}
          selectItems={profiles}
          useFormInstance={formInstance}
          formFieldName={profilesFieldName}
          selectAllFormName={selectAllFormName}
          disabled={disableProfilesSelector}
          {...(disabledProfileIds !== undefined ? { disabledProfileIds } : { disabledProfileIds: [] })}
          {...(selectedProfiles && selectedProfiles.length ? { selectedProfiles } : {})}
          {...(hasProfileError || hasPinterestTwitterProfileError
            ? getInputErrorMessageSeverityType(errorsProfile || errorsPinterestTwitter, ErrorMessageSeverity.error)
            : {})}
          {...(hasProfileWarning ? getInputErrorMessageSeverityType(errorsProfile, ErrorMessageSeverity.warning) : {})}
        />
      </div>
      {keyCategoryProfiles && tourtipCategoryProfileDesktopXY ? (
        <div style={{ zIndex: 10 }}>
          {/*<TourTip*/}
          {/*  id={keyCategoryProfiles}*/}
          {/*  tooltipPointerPosition={isMobile ? 'TopRight' : 'TopCenter'}*/}
          {/*  offsetY={tourtipCategoryProfileDesktopXY.top}*/}
          {/*  offsetX={tourtipCategoryProfileDesktopXY.left}*/}
          {/*  offsetFromDotY={isMobile ? 0 : -12}*/}
          {/*/>*/}
        </div>
      ) : null}
    </div>
  );
};
